import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Crown, Headphones, MessagesSquare, Sparkles, Type, X } from 'lucide-react';
import { useTrialStatus } from '../hooks/useFeatureAccess';
import { openUsageModal } from '../store/ui$';
import { trackEvent } from '../lib/analytics';
import { supabase } from '../lib/supabase';
import './TrialEndSummary.css';
import { Dialog, ICON_SIZE } from '../design-system';

const DISMISSED_KEY = 'reedle-trial-end-summary-dismissed';

/**
 * TrialEndSummary — full-screen personalized overlay shown once after trial expires.
 * Queries credit_usage_logs to show what Pro features the user actually used during trial.
 */
const TrialEndSummary = () => {
  const { t } = useTranslation('common');
  const { isTrialExpired, trialEnd, tier } = useTrialStatus();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Check if already dismissed
  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) {
      setDismissed(true);
    }
  }, []);

  // Fetch trial usage stats
  useEffect(() => {
    if (!isTrialExpired || dismissed || !trialEnd) {
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }

        // Calculate trial start (1 month before trial_end)
        const trialEndDate = new Date(trialEnd);
        const trialStartDate = new Date(trialEndDate);
        trialStartDate.setMonth(trialStartDate.getMonth() - 1);

        const { data, error } = await supabase
          .from('reedle_credit_usage_logs')
          .select('operation')
          .eq('user_id', user.id)
          .gte('created_at', trialStartDate.toISOString())
          .lte('created_at', trialEndDate.toISOString());

        if (error) { setLoading(false); return; }

        // Count by operation
        const counts = {};
        (data || []).forEach((row) => {
          counts[row.operation] = (counts[row.operation] || 0) + 1;
        });

        setStats({
          tts: counts.audio_generation || 0,
          chat: (counts.chat || 0) + (counts.ai_chat || 0) + (counts.learn_chat || 0),
          rag: counts.rag_chat || 0,
          translation: counts.translation || 0,
          summaries: counts.article_summary || 0,
          total: data?.length || 0,
        });
      } catch (err) {
        console.error('[TrialEndSummary] Error fetching stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [isTrialExpired, dismissed, trialEnd]);

  // Track impression once stats are loaded and component is visible
  const shouldShow = isTrialExpired && tier === 'free' && !dismissed && !loading && stats && stats.total >= 1;
  useEffect(() => {
    if (shouldShow && stats) {
      trackEvent('trial_end_summary_shown', { tts: stats.tts, chat: stats.chat, rag: stats.rag, total: stats.total });
    }
  }, [shouldShow, stats]);

  // Don't show if: still in trial, already paid, already dismissed, or no trial history
  if (!shouldShow) {
    return null;
  }

  const persistDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const handleUpgrade = () => {
    trackEvent('trial_end_summary_upgrade', { tts: stats?.tts, chat: stats?.chat, rag: stats?.rag, total: stats?.total });
    persistDismiss();
    openUsageModal('plan', 'trial_ended');
  };

  const dismiss = () => {
    trackEvent('trial_end_summary_dismissed', {});
    persistDismiss();
  };

  // Build personalized feature usage items
  const usageItems = [];
  if (stats?.tts > 0) {
    usageItems.push({
      icon: <Headphones size={ICON_SIZE.md} />,
      text: t('trial.summary.tts', { defaultValue: 'Listened to {{count}} articles with TTS', count: stats.tts }),
    });
  }
  if (stats?.chat > 0) {
    usageItems.push({
      icon: <MessagesSquare size={ICON_SIZE.md} />,
      text: t('trial.summary.chat', { defaultValue: 'Had {{count}} AI conversations', count: stats.chat }),
    });
  }
  if (stats?.rag > 0) {
    usageItems.push({
      icon: <BookOpen size={ICON_SIZE.md} />,
      text: t('trial.summary.rag', { defaultValue: 'Used Library Search {{count}} times', count: stats.rag }),
    });
  }
  if (stats?.translation > 0) {
    usageItems.push({
      icon: <Type size={ICON_SIZE.md} />,
      text: t('trial.summary.translation', { defaultValue: 'Translated {{count}} articles', count: stats.translation }),
    });
  }
  if (stats?.summaries > 0) {
    usageItems.push({
      icon: <Sparkles size={ICON_SIZE.md} />,
      text: t('trial.summary.summaries', { defaultValue: 'Generated {{count}} AI summaries', count: stats.summaries }),
    });
  }

  return (
    <Dialog
      isOpen
      onClose={dismiss}
      ariaLabel={t('trial.summary.title')}
      showClose={false}
      /* The backdrop was never a way out of this summary; only the buttons are. */
      closeOnBackdrop={false}
      overlayClassName="trial-end-overlay"
      className="trial-end-summary"
    >
      <button className="trial-end-close" onClick={dismiss}>
        <X size={ICON_SIZE.md} />
      </button>

      <div className="trial-end-icon">
        <Crown size={ICON_SIZE.xxl} />
      </div>

      <h2 className="trial-end-title">
        {t('trial.summary.title', { defaultValue: 'Your Pro trial has ended' })}
      </h2>

      {usageItems.length > 0 && (
        <>
          <p className="trial-end-subtitle">
            {t('trial.summary.subtitle', { defaultValue: 'During your trial, you:' })}
          </p>
          <div className="trial-end-stats">
            {usageItems.map((item, i) => (
              <div key={i} className="trial-end-stat">
                {item.icon}
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <p className="trial-end-pitch">
        {t('trial.summary.pitch', { defaultValue: 'Keep using these features and more for just $5/month.' })}
      </p>

      <button className="trial-end-cta" onClick={handleUpgrade}>
        <Crown size={ICON_SIZE.sm} />
        {t('trial.summary.upgrade', { defaultValue: 'Upgrade to Pro — $5/mo' })}
      </button>

      <button className="trial-end-dismiss" onClick={dismiss}>
        {t('trial.summary.continue', { defaultValue: 'Continue with Free' })}
      </button>
    </Dialog>
  );
};

export default TrialEndSummary;
